import { compose, filter, length, reject } from 'ramda';
import { isNilOrEmpty, isNilOrEmptyString } from 'ramda-extension';
import { createAction } from 'redux-actions';

import { replaceData, resetForm, setValue } from '@gef-ui/form-core/stream/transfer/control';

import { appendMode, REQUEST_SIZE, RESULTS_LIMIT } from 'constants/general';
import dialog from 'features/common/dialog';
import fetch from 'features/common/fetch';
import { saveFile } from 'features/common/fileSave/actions';
import { pushToFilterDataLayer } from 'features/common/gtm/utils';
import { FIELDS, POLICY_OVERVIEW_FILTER_FORM_NAME } from 'features/views/policyOverview/form';
import { getIsPPZSubordinate, getUser } from 'features/views/user/selectors';
import contextUserUtils from 'features/views/userContext';
import { generateCommonFetchActions, trimFormData } from 'utils';

import * as types from './actionTypes';
import { dialogs, formDataFieldsMap, SUBSELLER_NOT_FOUND_ERROR_MSG, timeout } from './constants';
import { getSearchedCriteriasFromAdvancedSearch, getSort } from './selectors';
import { bodyFactory } from './utils';

export const appendPolicies = createAction(types.APPEND_POLICIES, (policies) => ({ policies }));
export const reset = createAction(types.RESET);
export const resetWithoutSellerNumbers = createAction(types.RESET_WITHOUT_SELLER_NUMBERS);
export const resetWithoutSellerNumbersAndSearchedCriterias = createAction(
	types.RESET_WITHOUT_SELLER_NUMBERS_AND_SEARCHED_CRITERIAS,
);
export const setPolicies = createAction(types.SET_POLICIES, (policies) => ({ policies }));
export const setSort = createAction(types.SET_SORT, (sort) => ({ sort }));
export const setSellerNumbers = createAction(types.SET_SELLER_NUMBERS, (sellerNumbers) => ({ sellerNumbers }));
export const saveSearchedCriterias = createAction(types.SAVE_SEARCHED_CRITERIAS, (searchedCriterias) => ({
	searchedCriterias,
}));
export const removeFilterCriteriaByKey = createAction(types.REMOVE_FILTER_CRITERIA_BY_KEY, (key) => ({ key }));
export const saveFocusField = createAction(types.SAVE_FOCUS_FIELD, (key) => ({ key }));

const processFormData = compose(reject(isNilOrEmptyString), trimFormData);

const url = fetch.constants.urls.policyList;

const fetchContracts =
	({ body, queryParams, callGtmFilter }, append = appendMode.SET) =>
	async (dispatch, getState, { getFormData }) => {
		const contextUser = contextUserUtils.selectors.getContextUser(getState());
		const data = await getFormData(POLICY_OVERVIEW_FILTER_FORM_NAME);
		const formData = processFormData(data);

		dispatch(
			fetch.actions.post(url, {
				takeLatest: true,
				queryParams,
				body,
				additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
				onSuccess: [
					(response) => () => {
						const { data, pageMetaData } = response.body;
						if (!data || !Array.isArray(data)) return;

						if (pageMetaData.totalRows > RESULTS_LIMIT) {
							dispatch(dialog.actions.open(dialogs.tooManyResults));
							dispatch(resetWithoutSellerNumbersAndSearchedCriterias());
							return;
						}

						// GTM called only on basic search/filter, not on sort/loadMore/loadPage etc.
						if (callGtmFilter) {
							pushToFilterDataLayer({ filterSection: 'Přehled PS', formData, formDataFieldsMap });
						}
						dispatch(append === appendMode.APPEND ? appendPolicies(data) : setPolicies(data));
					},
				],
				onError: [
					(response) => () => {
						if (!response || !response.failure) return;
						const status = response.failure.status;

						if (status === 408) {
							dispatch(dialog.actions.open(dialogs.tooManyResults408));
						}

						if (status === 400) {
							dispatch(dialog.actions.open(dialogs.subsellerNotFound));
						}
					},
				],
				shouldReportError: ({ failure }) => {
					if (failure?.status === 400) {
						return failure?.parsedContent?.message !== SUBSELLER_NOT_FOUND_ERROR_MSG;
					} else {
						return failure.status !== timeout;
					}
				},
			}),
		);
	};

export const { loadSorted, loadMore, loadPage, reload } = generateCommonFetchActions(fetchContracts, url);

export const sortContracts = (sort) => (dispatch) => {
	dispatch(setSort(sort));
	dispatch(loadSorted(sort));
};

export const search =
	() =>
	async (dispatch, getState, { getFormData }) => {
		dispatch(setSort('policyOpenDate,desc'));
		dispatch(dialog.actions.clear());

		const data = await getFormData(POLICY_OVERVIEW_FILTER_FORM_NAME);
		const formData = processFormData(data);
		const body = bodyFactory(formData);
		const sort = getSort(getState());

		const showSubsellerAgencyNoAndWithSubordinatesConflict = compose(
			(finalLength) => finalLength > 1,
			length,
			filter((item) => !!item),
		)([!isNilOrEmpty(body?.sellerIns), !isNilOrEmpty(body?.sellerLogin), body?.searchAcrossSubordinates]);

		const queryParams = { size: REQUEST_SIZE, page: 0, sort };

		if (!formData.gcpContractsOnly && !formData.gpContractsOnly) {
			return dispatch(dialog.actions.open(dialogs.atLeastOneContractTypeCheckboxSelected));
		}

		if (showSubsellerAgencyNoAndWithSubordinatesConflict) {
			dispatch(dialog.actions.open(dialogs.subsellerLoginAgencyNoAndWithSubordinatesConflict));
		} else {
			dispatch(saveSearchedCriterias(formData));
			dispatch(fetchContracts({ body, queryParams, callGtmFilter: true }));
		}
	};
export const setInitialFormValues =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		const isPPZSubordinate = getIsPPZSubordinate(getState());
		const userLogon = getUser(getState());

		sendControlNext(
			POLICY_OVERVIEW_FILTER_FORM_NAME,
			setValue({ fieldName: FIELDS.gcpContractsOnly, value: true }),
		);
		sendControlNext(POLICY_OVERVIEW_FILTER_FORM_NAME, setValue({ fieldName: FIELDS.gpContractsOnly, value: true }));
		sendControlNext(
			POLICY_OVERVIEW_FILTER_FORM_NAME,
			setValue({ fieldName: FIELDS.frameworkPolicyOnly, value: false }),
		);
		sendControlNext(
			POLICY_OVERVIEW_FILTER_FORM_NAME,
			setValue({ fieldName: FIELDS.activePolicyWoQuotationOnly, value: false }),
		);

		// setting up sellerLogin's initialValue to form
		if (isPPZSubordinate) {
			sendControlNext(
				POLICY_OVERVIEW_FILTER_FORM_NAME,
				setValue({ fieldName: FIELDS.sellerLogin, value: userLogon }),
			);
			dispatch(saveSearchedCriterias({ [FIELDS.sellerLogin]: userLogon }));
		}
	};

export const resetToInitialState = () => (dispatch) => {
	dispatch(reset());
	dispatch(fetch.actions.clearFetchStore(url));
};

export const resetCriteria =
	() =>
	(dispatch, getState, { sendControlNext }) => {
		sendControlNext(POLICY_OVERVIEW_FILTER_FORM_NAME, resetForm());
		dispatch(setInitialFormValues());
		dispatch(fetch.actions.clearFetchStore(url));
		dispatch(actions.resetWithoutSellerNumbers());
	};

export const resetOnlyAdvancedSearchForm =
	() =>
	async (dispatch, getState, { sendControlNext, getFormData }) => {
		const originalData = await getFormData(POLICY_OVERVIEW_FILTER_FORM_NAME);
		const searchedCriteriasFromAdvancedSearch = getSearchedCriteriasFromAdvancedSearch(getState());

		const dataReset = {
			data: {
				...originalData,
				...searchedCriteriasFromAdvancedSearch,
			},
		};

		sendControlNext(POLICY_OVERVIEW_FILTER_FORM_NAME, replaceData(dataReset));
	};

export const getUsersSellerNumbers = (login) => (dispatch) => {
	dispatch(
		fetch.actions.get(fetch.constants.urls.userSellerIns, {
			queryParams: {
				login,
				filterCompany: true,
			},
			onSuccess: [
				(response) => () => {
					if (!response || !response.body) return;
					const activeSellerNumbers = response.body.map((sellerNumber) => sellerNumber.sellerIn);

					dispatch(setSellerNumbers(activeSellerNumbers));
				},
			],
		}),
	);
};

export const exportPolicies = () => async (dispatch, getState) => {
	const { body } = fetch.selectors.getLastRequest(url)(getState());
	const contextUser = contextUserUtils.selectors.getContextUser(getState());

	await dispatch(
		fetch.actions.postBinary(fetch.constants.urls.policyListExport, {
			body,
			onSuccess: [
				(response) => () => {
					saveFile({ response });
				},
			],
			additionalHeaders: contextUser ? { 'X-Context-User': contextUser.login } : {},
		}),
	);
};

export const actions = {
	reset,
	resetWithoutSellerNumbers,
	resetCriteria,
	search,
};
